/**
 * Page State Management
/**
 * @typedef {Object} MenuItem
 * @property {string} menuResponse - The name of the menu item
 * @property {string[]} imageUrls - Array of image URLs
 * @property {number|null} nutritional_score - Nutritional score
 * @property {number|null} approximate_calories - Estimated calories
 * @property {boolean|null} is_vegetarian - Whether item is vegetarian
 * @property {boolean|null} is_vegan - Whether item is vegan
 * @property {boolean|null} is_glutenFree - Whether item is gluten-free
 * @property {boolean|null} is_dairyFree - Whether item is dairy-free
 * @property {string|null} price - Price of the item
 * @property {string|null} description - Description of the item
 * @property {boolean} [loading] - Whether item is still loading (for placeholders)
 */

/**
 * @typedef {Object} IdleState
 * @property {'idle'} status - No image selected
 * @property {string} sessionId - Session ID for sharing
 */

/**
 * @typedef {Object} ReadyState
 * @property {'ready'} status - Image selected, ready to process
 * @property {string} sessionId - Session ID for sharing
 */

/**
 * @typedef {Object} ProcessingState
 * @property {'processing'} status - Currently processing
 * @property {number} progress - Progress percentage (0-100)
 * @property {string} sessionId - Session ID for sharing
 */

/**
 * @typedef {Object} SuccessState
 * @property {'complete'} status - Processing completed successfully
 * @property {MenuItem[]} items - The processed menu items
 * @property {string} sessionId - Session ID for sharing
 */

/**
 * @typedef {Object} ErrorState
 * @property {'error'} status - Processing failed
 * @property {string} error - Error title/message
 */

/**
 * @typedef {IdleState | ReadyState | ProcessingState | SuccessState | ErrorState} PageState
 */

// State creation helpers

export const createIdleState = () => ({ status: 'idle' });

export const createReadyState = () => ({ status: 'ready' });

export const createProcessingState = ({ items, sessionId }) => ({
  status: 'processing',
  items,
  sessionId,
});

export const createCompletedState = (items, sessionId) => ({
  status: 'complete',
  items,
  sessionId,
});

export const createErrorState = (error, message) => ({
  status: 'error',
  error,
  message,
});

// State type guards
export const isIdle = state => state.status === 'idle';
export const isReady = state => state.status === 'ready';
export const isProcessing = state => state.status === 'processing';
export const isCompleted = state => state.status === 'complete';
export const isError = state => state.status === 'error';

// Convenience checks
export const hasItems = state => (isProcessing(state) || isCompleted(state)) && state?.items?.length > 0;
export const canProcess = state => isReady(state);
export const isLoading = state => isProcessing(state);
export const shouldShowResults = state => isProcessing(state) || isCompleted(state) || isError(state);
